import React, { useState } from "react";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  Col,
  Container,
  Row,
  Badge,
  UncontrolledTooltip,
} from "reactstrap";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import AdminHeader from "components/Headers/AdminHeader";
import CommonTable from "components/CommonComponents/ReactTable/Table";
import { DEFAULT_RECORDS_PER_PAGE } from "components/constant";
import { showAlert } from "components/CommonComponents/CommonFunctions";
import { urlDecodedId } from "components/CommonComponents/encodeDecodeFunction";

/* STATIC DATA (TEMP – replace with API later) */
const traineeList = [
  {
    id: 1,
    traineeName: "Rahul Sharma",
    traineeEmail: "rahul.sharma@mail.com",
    attendance: null,
  },
  {
    id: 2,
    traineeName: "Neha Patil",
    traineeEmail: "neha.patil@mail.com",
    attendance: "Present",
  },
  {
    id: 3,
    traineeName: "Amit Verma",
    traineeEmail: "amit.verma@mail.com",
    attendance: "Absent",
  },
  {
    id: 4,
    traineeName: "Pooja Mehta",
    traineeEmail: "pooja.mehta@mail.com",
    attendance: "Absent",
  },
];

function SessionAttendance() {
  const { id } = useParams();
  const decodedId = id ? urlDecodedId(id) : null;
  const location = useLocation();
  const { sessionName, courseName } = location.state || {};
  const navigate = useNavigate();

  const [pageNum, setPageNum] = useState(1);
  const [trainees, setTrainees] = useState(traineeList);
  const [proofFile, setProofFile] = useState(null);

  const updateAttendance = (id, status) => {
    setTrainees((prev) =>
      prev.map((t) => (t.id === id ? { ...t, attendance: status } : t)),
    );
  };

  const handleProofUploadClick = (e) => {
    e.stopPropagation();

    const input = document.getElementById("supportingDocs");
    if (input) {
      input.click();
    }
  };

  const handleSubmitAttendance = () => {
    const unmarkedExists = trainees.some(
      (t) => t.attendance !== "Present" && t.attendance !== "Absent",
    );

    if (unmarkedExists) {
      showAlert({
        type: "warning",
        title: "Please mark attendance for all trainees before submitting.",
        confirmText: "OK",
        confirmStyle: "deep-blue",
      });
      return;
    }

    const total = trainees.length;
    const presentCount = trainees.filter(
      (t) => t.attendance === "Present",
    ).length;
    const absentCount = trainees.filter(
      (t) => t.attendance === "Absent",
    ).length;

    showAlert({
      type: "warning",
      title: (
        <div className="text-left">
          <h3 className="mb-1 text-center">
            <span className="text-muted">Total trainees:</span>{" "}
            <strong>{total}</strong> <br />{" "}
            <span className="text-muted">Present trainees:</span>{" "}
            <strong> {presentCount} | </strong>{" "}
            <span className="text-muted">Absent trainees:</span>{" "}
            <strong> {absentCount}</strong>
          </h3>

          {/* Upload UI */}
          <div className="border rounded">
            <div
              className="d-flex flex-column align-items-center mt-4"
              onClick={handleProofUploadClick}
            >
              <i className="fa fa-cloud-upload fa-2x text-muted mb-2" />

              <p className="mb-1 text-muted">Click to upload proof</p>

              <span className="btn btn-sm btn-color mb-3">Choose Files</span>

              <input
                type="file"
                id="supportingDocs"
                className="d-none"
                onChange={(e) => setProofFile(e.target.files[0])}
              />
            </div>
          </div>

          {proofFile && (
            <p className="text-success text-center mt-2 mb-0">
              Selected file: <strong>{proofFile.name}</strong>
            </p>
          )}

          <p className="mt-3 text-center">
            Are you sure you want to submit the attendance?
          </p>
        </div>
      ),
      confirmText: "Submit",
      cancelText: "Cancel",
      confirmStyle: "deep-blue",
      cancelStyle: "secondary",
    });
  };

  const markAllPresent = () => {
    showAlert({
      title: "Are you sure you want to mark all trainees as Present?",
      text: "This will mark attendance as Present for all trainees.",
      type: "warning",
      confirmText: "Mark All",
      cancelText: "Cancel",
      confirmStyle: "deep-blue",
      cancelStyle: "secondary",
      showCancel: true,
      onConfirm: () => {
        setTrainees((prev) =>
          prev.map((t) => ({ ...t, attendance: "Present" })),
        );
      },
    });
  };

  const columns = [
    {
      Header: "Sr. No.",
      accessor: "id",
    },
    {
      Header: "Trainee Name",
      accessor: "traineeName",
    },
    {
      Header: "Trainee Email",
      accessor: "traineeEmail",
    },
    // {
    //   Header: "Attendance Status",
    //   accessor: "attendance",
    //   Cell: ({ value }) => {
    //     return (
    //       <Badge color={value === "Present" ? "success" : "danger"} pill>
    //         {value}
    //       </Badge>
    //     );
    //   },
    // },
    {
      Header: "Mark Attendance",
      disableSortBy: true,
      Cell: ({ row }) => {
        const { id, attendance } = row.original;

        const isPresent = attendance === "Present";
        const isAbsent = attendance === "Absent";

        return (
          <div className="d-flex gap-4 align-items-center">
            {/* PRESENT */}
            <Button
              size="sm"
              className="p-0 d-flex align-items-center gap-1"
              id={`present-${id}`}
              onClick={(e) => {
                e.stopPropagation();
                updateAttendance(id, "Present");
              }}
            >
              <i
                className={`fa-regular ${
                  isPresent
                    ? "fa-circle-check attendance-icon-success"
                    : "fa-circle attendance-icon-muted"
                }`}
              />
              <i
                className={`fa-solid fa-p ${
                  isPresent
                    ? "attendance-letter-success"
                    : "attendance-letter-muted"
                }`}
              />
            </Button>

            <UncontrolledTooltip delay={0} target={`present-${id}`}>
              Mark Present
            </UncontrolledTooltip>

            {/* ABSENT */}
            <Button
              size="sm"
              className="p-0 d-flex align-items-center gap-1"
              id={`absent-${id}`}
              onClick={(e) => {
                e.stopPropagation();
                updateAttendance(id, "Absent");
              }}
            >
              <i
                className={`fa-regular ${
                  isAbsent
                    ? "fa-circle-check attendance-icon-red"
                    : "fa-circle attendance-icon-muted"
                }`}
              />
              <i
                className={`fa-solid fa-a ${
                  isAbsent ? "attendance-letter-red" : "attendance-letter-muted"
                }`}
              />
            </Button>

            <UncontrolledTooltip delay={0} target={`absent-${id}`}>
              Mark Absent
            </UncontrolledTooltip>
          </div>
        );
      },
    },
  ];

  const markedCount = trainees.filter((t) => t.attendance === "Present").length;

  return (
    <>
      <AdminHeader />
      <Container fluid className="mt--6">
        <Row>
          <Col md="12">
            <div
              className="d-flex align-items-center cursor text-white"
              onClick={() => navigate(-1)}
            >
              <i className="fa-solid fa-angle-left mr-2" />
              <span className="font-weight-bold">Back</span>
            </div>
            <Card className="mb-3 mt-3">
              <CardHeader className="d-flex justify-content-between align-items-center">
                <CardTitle tag="h3" className="mb-0">
                  <Row className="mb-1">
                    <Col xs="auto" className="mr-3">
                      <span className="text-muted">Session</span>
                      <div className="font-weight-bold text-wrap mt--2">
                        {sessionName}
                      </div>
                    </Col>

                    <Col xs="auto">
                      <span className="text-muted">Course</span>
                      <div className="font-weight-bold text-wrap mt--2">
                        {courseName}
                      </div>
                    </Col>
                  </Row>

                  <div className="text-muted mt-3">
                    Tuesday, 21 Jan 2026 · 11:30 AM
                  </div>
                </CardTitle>
              </CardHeader>

              <CardBody>
                <div className="mb-3">
                  <strong>
                    Attendance Marked: {markedCount} / {trainees.length}
                  </strong>
                  <div className="d-flex justify-content-end mt--3">
                    <Button className="btn-deep-blue" onClick={markAllPresent}>
                      Mark all Present
                    </Button>
                    <Button
                      className="btn-deep-blue"
                      onClick={handleSubmitAttendance}
                    >
                      Submit Attendance
                    </Button>
                  </div>
                </div>

                <CommonTable
                  columns={columns}
                  data={trainees}
                  defaultPageSize={DEFAULT_RECORDS_PER_PAGE || 10}
                  activePage={pageNum}
                  setPageNum={setPageNum}
                  totalRecords={trainees.length}
                  showPaginationBottom={true}
                />
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </>
  );
}

export default SessionAttendance;
